import type { FC } from 'react';
import { useCallback } from 'react';
import useEnqueueSnackbar from '../../../hooks/useEnqueueSnackbar';
import copy from 'copy-to-clipboard';
import { Box, Button } from '@mui/material';
import CopyAllOutlined from '@mui/icons-material/CopyAllOutlined';
import IconGoogleColor from 'assets/icon-google-color.svg?react';
import type { GoogleCreativeFormat } from '../api/types';
import useFileDownload from '../hooks/useFileDownload';
import GoogleContentPreviewMoreMenu from './GoogleContentPreviewMoreMenu';
import GoogleVideoOpenMenu from './GoogleVideoOpenMenu';
import GoogleImageOpenMenu from './GoogleImageOpenMenu';

interface IProps {
  creativeId: string;
  adLibraryUrl: string;
  contentUrl?: string;
  previewUrl?: string;
  sourceVideoUrl?: string;
  storedVideoUrl?: string;
  sourceImageUrl?: string;
  storedImageUrl?: string;
  containerId?: string;
  position?: 'top' | 'bottom';
  mediaType?: GoogleCreativeFormat;
}

const btnWhite = { color: 'white', fontSize: 10 } as const;
const iconSx = { mr: -0.5 } as const;

const GoogleContentPreviewButtons: FC<IProps> = ({
  creativeId,
  adLibraryUrl,
  contentUrl,
  previewUrl,
  sourceVideoUrl,
  storedVideoUrl,
  sourceImageUrl,
  storedImageUrl,
  containerId,
  mediaType,
  position = 'bottom',
}) => {
  const { onShowAlert } = useEnqueueSnackbar();
  const previewDownload = useFileDownload();
  const videoDownload = useFileDownload();
  const imageDownload = useFileDownload();

  // the nav panel only shows on hover — while a dropdown is open the pointer sits on the menu, so pin it
  const handlePanelPinnedChange = useCallback(
    (pinned: boolean) => {
      const targetContainerId = containerId ?? `google-ads-creative-${creativeId}-paper`;
      const paperContainer = document.querySelector(`#${targetContainerId}`);
      paperContainer?.classList.toggle('visibility', pinned);
    },
    [creativeId, containerId]
  );

  const handleCopyCreoUrl = useCallback(() => {
    if (!contentUrl) return;
    copy(contentUrl);
    onShowAlert(Date.now(), 'Creo URL has been copied to clipboard!');
  }, [contentUrl, onShowAlert]);

  const handleCopyPreviewUrl = useCallback(() => {
    if (!previewUrl) return;
    copy(previewUrl);
    onShowAlert(Date.now(), 'Preview URL has been copied to clipboard!');
  }, [previewUrl, onShowAlert]);

  const handleDownloadPreview = useCallback(() => {
    void previewDownload.download(previewUrl, `google-creative-${creativeId}-preview`);
  }, [previewDownload, previewUrl, creativeId]);

  const handleDownloadVideo = useCallback(() => {
    void videoDownload.download(storedVideoUrl, `google-creative-${creativeId}-video`);
  }, [videoDownload, storedVideoUrl, creativeId]);

  const handleDownloadImage = useCallback(() => {
    void imageDownload.download(storedImageUrl, `google-creative-${creativeId}-image`);
  }, [imageDownload, storedImageUrl, creativeId]);

  const handleOpenGoogle = () => {
    window.open(adLibraryUrl, '_blank');
  };

  return (
    <Box
      className="creative-nav-panel"
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        px: 1,
        position: 'absolute',
        ...(position === 'bottom' ? { bottom: 6 } : { top: 6 }),
        left: 6,
        right: 6,
        height: 42,
        margin: 'auto',
        zIndex: 2,
        backgroundColor: 'rgba(0,0,0, 0.5)',
        borderRadius: '4px',
      }}
    >
      <Button
        onClick={handleCopyCreoUrl}
        variant="text"
        size="small"
        startIcon={<CopyAllOutlined sx={iconSx} />}
        disabled={!contentUrl}
        sx={{ ...btnWhite, lineHeight: '1', textAlign: 'left' }}
      >
        Creo url
      </Button>
      {mediaType === 'video' ? (
        <GoogleVideoOpenMenu
          sourceVideoUrl={sourceVideoUrl}
          storedVideoUrl={storedVideoUrl}
          position={position}
          downloadProgress={videoDownload.progress}
          onOpenChange={handlePanelPinnedChange}
          onDownloadVideo={handleDownloadVideo}
        />
      ) : (
        <GoogleImageOpenMenu
          sourceImageUrl={sourceImageUrl}
          storedImageUrl={storedImageUrl}
          position={position}
          downloadProgress={imageDownload.progress}
          onOpenChange={handlePanelPinnedChange}
          onDownloadImage={handleDownloadImage}
        />
      )}
      <Button
        onClick={handleOpenGoogle}
        variant="text"
        size="small"
        startIcon={<IconGoogleColor width={14} height={14} style={{ marginRight: -4 }} />}
        sx={btnWhite}
      >
        Google
      </Button>
      <GoogleContentPreviewMoreMenu
        previewUrl={previewUrl}
        position={position}
        downloadProgress={previewDownload.progress}
        onOpenChange={handlePanelPinnedChange}
        onCopyPreviewUrl={handleCopyPreviewUrl}
        onDownloadPreview={handleDownloadPreview}
      />
    </Box>
  );
};

export default GoogleContentPreviewButtons;
